import { useEffect, useState, Suspense, useMemo, useCallback } from 'react';

import { GraphQLClient } from 'graphql-request';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { AppContext } from './App.context';
import { useDb, useWorkerMessages, useInstalled, useResize } from './App.hooks';
import config from './config.window';

import {
	useDeviceInformation,
	useDocumentTitle,
	usePrintStatus,
	useDialog,
	useIsOnline,
} from '@/hooks';

import { Password, usePassword } from '@views/password/Password';
import { getGraphQLClient } from '@views/home/auth/Authentication.hooks';

import { About } from '@components/about/About';

import LocalStorageKV from '@services/kv/LocalStorageKV';
import {
	QueryRequest,
	ReactQueryDevtoolsProduction,
	defaultQueryClient,
} from '@services/query-client/QueryClient';

import { Assets } from '@/assets';

import { ALERT_TIMEOUT_KEYS } from '@/constants';
import { Tracking } from '@/tracking/Tracking';
import Template from '@/templates/Template';
import { ServiceWorkerProvider } from '@/services/sw/sw.context';

import './App.css';

declare global {
	interface Window {
		onbeforeinstallprompt?: any;
		toggleDevtools?: any;
	}
}

function App() {
	const isOnline = useIsOnline();
	const { contentProtection } = config.app;
	const [showDevtools, setShowDevtools] = useState(false);
	const device = useDeviceInformation();
	const { appKey, languages, language, title } = config.build;

	const [graphQLClient, setGraphQLClient] =
		useState<GraphQLClient>(getGraphQLClient());

	const [queryClient, setQueryClient] =
		useState<QueryClient>(defaultQueryClient);

	useBoot();

	useEffect(() => {
		queryClient.setDefaultOptions({
			queries: {
				...queryClient.getDefaultOptions().queries,
				networkMode: isOnline ? 'online' : 'offlineFirst',
			},
			mutations: {
				...queryClient.getDefaultOptions().mutations,
				networkMode: isOnline ? 'online' : 'offlineFirst',
			},
		});
		setQueryClient(queryClient);
	}, [isOnline]);

	const kv = useMemo(() => new LocalStorageKV('cf-kv'), []);

	const isSupportInstallPrompt =
		typeof window.onbeforeinstallprompt === 'object';

	const [contentCSS, setContentCSS] = useState('');
	const [deferredPrompt, setDeferredPrompt] = useState<any>(undefined);

	const isInstalled = useInstalled();
	const { shouldDisplayPassword, onSuccessPassword } = usePassword();
	const { show: showDialog, setShowDialog } = useDialog();

	const isTouchable = useMemo(
		() => window.matchMedia('(pointer: coarse)').matches,
		[],
	);

	const clearCacheFunction = useMemo(() => clearCache(), []);

	useEffect(() => {
		window.toggleDevtools = () => setShowDevtools((old) => !old);
	}, []);
	useEffect(() => {
		localStorage.setItem('app-mode', `${import.meta.env.MODE}`);

		if (isSupportInstallPrompt) {
			window.addEventListener('beforeinstallprompt', (e) => {
				// Prevents the default mini-infobar or install dialog from appearing on mobile
				e.preventDefault();
				setDeferredPrompt(e);
				return false;
			});
		}
		if (contentProtection) {
			document.body.oncontextmenu = (e) => {
				e.preventDefault();
				return false;
			};
			setContentCSS(`@media print {
				html, body {
					   display: none;  /* hide whole page */
				}
			}`);
		}

		// Clear timeouts
		if (sessionStorage.getItem(ALERT_TIMEOUT_KEYS.ID)) {
			sessionStorage.removeItem(ALERT_TIMEOUT_KEYS.ID);
		}

		if (sessionStorage.getItem(ALERT_TIMEOUT_KEYS.DATETIME)) {
			sessionStorage.removeItem(ALERT_TIMEOUT_KEYS.DATETIME);
		}
	}, []);
	useResize();
	useDocumentTitle(title);

	const isPrinting = usePrintStatus();
	const { db } = useDb();

	useWorkerMessages({ db });

	const [lang, setLang] = useState(
		localStorage.getItem('language')
			? localStorage.getItem('language')
			: language,
	);

	const onLanguageChange = useCallback((language: string) => {
		localStorage.setItem('language', language);
		setLang(language);
	}, []);

	const rebuildGraphQLClient = useCallback(() => {
		setGraphQLClient(getGraphQLClient());
	}, []);

	const parseGraphQLClientError = useCallback((err: any) => {
		try {
			const reqRes = JSON.parse(JSON.stringify(err));
			return new Error(reqRes.response.errors[0].message);
		} catch (e) {
			throw err;
		}
	}, []);

	const queryRequest: QueryRequest = useMemo(
		() => new QueryRequest(queryClient, graphQLClient),
		[queryClient, graphQLClient],
	);

	queryRequest.rebuild = () => {
		setTimeout(() => {
			rebuildGraphQLClient();
		}, 100);
	};

	const contextValue = useMemo(
		() => ({
			db: db ?? undefined,
			appKey,
			graphQLClient,
			baseUrl: config?.build?.subdirectory ?? '/',
			lang: lang ?? 'en',
			languages,
			queryRequest,
			onLanguageChange,
			onlineStatus: !!isOnline,
			isPrinting,
			isInstalled,
			device,
			isTouchable,
			isSupportInstallPrompt,
			deferredPrompt,
			kv,
			clearCache: clearCacheFunction,
			rebuildGraphQLClient,
			parseGraphQLClientError,
		}),
		[
			db,
			appKey,
			device,
			graphQLClient,
			lang,
			languages,
			queryRequest,
			onLanguageChange,
			isOnline,
			isPrinting,
			isTouchable,
			isSupportInstallPrompt,
			deferredPrompt,
			kv,
			clearCacheFunction,
			rebuildGraphQLClient,
			parseGraphQLClientError,
		],
	);

	if (isOnline === null || !db || !kv) {
		return null;
	}

	if (shouldDisplayPassword) {
		return <Password onSuccess={onSuccessPassword} />;
	}

	return (
		<ServiceWorkerProvider>
			<AppContext.Provider value={contextValue}>
				<QueryClientProvider client={queryClient}>
					<Tracking>
						<Template />
					</Tracking>

					{contentCSS ? <style>{contentCSS}</style> : null}
					<About
						show={showDialog}
						setShow={(v: boolean) => {
							setShowDialog(v);
						}}
					/>

					<ReactQueryDevtools
						initialIsOpen={false}
						position="right"
						buttonPosition="bottom-left"
					/>

					{showDevtools && (
						<Suspense fallback={null}>
							<ReactQueryDevtoolsProduction
								position="right"
								buttonPosition="bottom-left"
							/>
						</Suspense>
					)}
				</QueryClientProvider>
			</AppContext.Provider>
		</ServiceWorkerProvider>
	);
}

async function clearCache(): Promise<void> {
	const promises = [];
	const keyList = await caches.keys();
	for (const key of keyList) {
		promises.push(caches.delete(key));
	}

	await Promise.all(promises);
}

function useBoot() {
	const setBuildValues = () => {
		localStorage.setItem('app_key', config.build.appKey);
		localStorage.setItem('version', config.build.version);
	};
	useEffect(() => {
		const version = localStorage.getItem('version');
		const appKey = localStorage.getItem('app_key');
		if (!appKey || !version) {
			clearLocalStorage();
			setBuildValues();
			return;
		}

		if (
			appKey !== config.build.appKey ||
			version !== config.build.version
		) {
			clearLocalStorage();
			setBuildValues();
			return;
		}
	}, []);
}

function clearLocalStorage() {
	const ignoredKeys = new Set(['favorites']);
	for (const key in localStorage) {
		if (ignoredKeys.has(key)) {
			continue;
		}

		localStorage.removeItem(key);
	}
}

export default App;
